import { AKAMAI_ZIP_CODE_LOCALE_VALIDATION_CONFIGURATION } from "../../../utils/validations/akamaiZipCode.js";
import { WAREHOUSE_TYPES } from "../../../utils/warehouses/constants.js";
import { getWarehouseType } from "../../../utils/warehouses/getWarehouseType.js";
import { milesToKm } from "../../../utils/warehouses/warehouseHelpers.js";
import { DISTANCE_UNIT } from "../WarehouseList/constants.js";
function isShowRoom(warehouse) {
  return getWarehouseType(warehouse) === WAREHOUSE_TYPES.SHOWROOM;
}
function isBusinessCenter(warehouse) {
  return getWarehouseType(warehouse) === WAREHOUSE_TYPES.BUSINESS_CENTER;
}
function canBeSetAsPreferredWarehouse(warehouse) {
  return getWarehouseType(warehouse) === WAREHOUSE_TYPES.STANDARD;
}
const getWarehouseName = (warehouse) => {
  var _a, _b;
  if ((_a = warehouse == null ? void 0 : warehouse.name[0]) == null ? void 0 : _a.value) {
    return (_b = warehouse == null ? void 0 : warehouse.name[0]) == null ? void 0 : _b.value;
  }
  return "";
};
const movePreferredWarehouse = (warehouses, preferredWarehouse) => {
  const preferredWarehouseIndex = warehouses.findIndex(
    (warehouse) => warehouse.warehouseId === preferredWarehouse
  );
  if (preferredWarehouseIndex > -1) {
    const preferredWarehouseObject = warehouses[preferredWarehouseIndex];
    const remainingWarehouses = warehouses.filter(
      (_warehouse, index) => index !== preferredWarehouseIndex
    );
    return [preferredWarehouseObject, ...remainingWarehouses];
  }
  return warehouses;
};
const sortWarehouses = (warehouses, preferredWarehouse) => {
  if (preferredWarehouse && warehouses.some(
    (warehouse) => warehouse.warehouseId === preferredWarehouse
  )) {
    return movePreferredWarehouse(warehouses, preferredWarehouse);
  } else {
    return warehouses;
  }
};
const deduplicateWarehouses = (warehouses) => {
  const seen = /* @__PURE__ */ new Set();
  return warehouses.filter((warehouse) => {
    if (seen.has(warehouse.warehouseId)) {
      return false;
    }
    seen.add(warehouse.warehouseId);
    return true;
  });
};
const combineAndDeduplicateWarehouses = (...warehouseLists) => {
  const combined = warehouseLists.flat();
  const deduplicated = deduplicateWarehouses(combined);
  return deduplicated.sort((a, b) => {
    const distanceA = a.distance ?? Infinity;
    const distanceB = b.distance ?? Infinity;
    return distanceA - distanceB;
  });
};
const convertWarehouseDistances = (warehouses, locale) => {
  const targetUnit = DISTANCE_UNIT[locale.toLowerCase()] || DISTANCE_UNIT["en-us"];
  return warehouses.map((warehouse) => {
    if (!warehouse.distance || warehouse.distanceUnit === targetUnit) {
      return warehouse;
    }
    if (warehouse.distanceUnit === "mi" && targetUnit === "km") {
      const convertedDistance = Number(milesToKm(warehouse.distance));
      return {
        ...warehouse,
        distance: convertedDistance,
        distanceUnit: "km"
      };
    }
    return warehouse;
  });
};
const getSortedWarehouses = async ({
  agent,
  filters,
  locale,
  location,
  preferredWarehouse
}) => {
  var _a;
  const payload = await agent.getWarehousesByLatLng(
    location == null ? void 0 : location.latitude,
    location == null ? void 0 : location.longitude,
    locale,
    false,
    filters
  );
  if (payload) {
    const { context, warehouses } = payload;
    if (((_a = context == null ? void 0 : context.statusMessage) == null ? void 0 : _a.statusCode) === "Success" && (warehouses == null ? void 0 : warehouses.length) > 0) {
      const convertedWarehouses = convertWarehouseDistances(
        warehouses,
        locale
      );
      const sortedWarehouses = sortWarehouses(
        convertedWarehouses,
        preferredWarehouse
      );
      return sortedWarehouses;
    } else {
      return [];
    }
  } else {
    return [];
  }
};
const getWarehousesWithGasPrices = async ({
  agent,
  sortedWarehouses
}) => {
  if (!sortedWarehouses.length) {
    return [];
  }
  try {
    const warehouseIds = sortedWarehouses.map(
      (warehouseObj) => warehouseObj.warehouseId
    );
    const gasPrices = await agent.getWarehousesGasPrices(warehouseIds);
    if (gasPrices) {
      return sortedWarehouses.map((warehouse) => ({
        ...warehouse,
        gasPrices: warehouse.warehouseId in gasPrices ? gasPrices[Number(warehouse.warehouseId)] : null
      }));
    }
    return sortedWarehouses.map((warehouse) => ({
      ...warehouse,
      gasPrices: null
    }));
  } catch (error) {
    console.error("Error fetching gas prices:", error);
    return sortedWarehouses.map((warehouse) => ({
      ...warehouse,
      gasPrices: null
    }));
  }
};
const getPreferredWarehouse = (nearestWarehouse) => {
  if (nearestWarehouse) {
    const catalog = nearestWarehouse.catalog;
    if (catalog) {
      return catalog.slice(0, -3);
    }
  }
  return void 0;
};
const getCountryFromLocale = (locale) => {
  return locale.split("-")[1].toUpperCase();
};
const removeDuplicateAddresses = (suggestions) => {
  const unique = {};
  suggestions.forEach((suggestion) => {
    if (!unique[suggestion.address]) {
      unique[suggestion.address] = suggestion;
    }
  });
  return Object.values(unique);
};
const getOptionLabel = (option) => {
  const optionsTyped = option;
  return optionsTyped.address ?? "";
};
function getValidationSchema(locale) {
  var _a;
  return (_a = AKAMAI_ZIP_CODE_LOCALE_VALIDATION_CONFIGURATION[locale]) == null ? void 0 : _a.validationSchema;
}
export {
  canBeSetAsPreferredWarehouse,
  combineAndDeduplicateWarehouses,
  convertWarehouseDistances,
  deduplicateWarehouses,
  getCountryFromLocale,
  getOptionLabel,
  getPreferredWarehouse,
  getSortedWarehouses,
  getValidationSchema,
  getWarehouseName,
  getWarehousesWithGasPrices,
  isBusinessCenter,
  isShowRoom,
  movePreferredWarehouse,
  removeDuplicateAddresses,
  sortWarehouses
};
